import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

// iter 277: CI inventory cell. Shows the qty available to this viewer
// (CI − other quotations' reservations) and a small "−N reserved" badge
// that opens a popover listing who holds what.
export function CiQtyCell({ row, align = "right", onChanged }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const canClear = user && ["admin", "ci_admin"].includes(user.role);
  const [detail, setDetail] = useState(null);
  const ci = row.ci_inventory_qty;
  const other = row.ci_reserved_other || 0;
  const mine = row.ci_reserved_mine || 0;
  const total = row.ci_reserved_total || 0;
  const available = row.ci_available ?? ci;
  if (ci == null) return <span className="text-gray-400">-</span>;
  if (!total) return <span data-testid={`ci-available-${row.entry_id}`}>{ci}</span>;
  const load = async (open) => {
    if (!open) return;
    try { const { data } = await api.get(`/ci-reservations/entry/${row.entry_id}`); setDetail(data); } catch { setDetail({ items: [] }); }
  };
  const clear = async (r) => {
    if (!window.confirm(t("ci.clear_confirm", { name: r.cart_name, qty: r.qty }))) return;
    try { await api.post(`/ci-reservations/${r.id}/release`, { reason: "cleared by CI admin" }); toast.success(t("ci.released")); await load(true); if (onChanged) onChanged(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("samples.failed")); }
  };
  return (
    <Popover onOpenChange={load}>
      <PopoverTrigger asChild>
        <button type="button" className={`inline-flex flex-col items-${align === "right" ? "end" : "start"} leading-tight group`} data-testid={`ci-reserved-badge-${row.entry_id}`} onClick={(e) => e.stopPropagation()}>
          <span className="font-mono text-[14px] text-gray-800" data-testid={`ci-available-${row.entry_id}`}>{available}</span>
          <span className={`text-[10px] px-1 rounded border ${mine ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"} group-hover:underline`}>
            −{total} {t("ci.reserved")}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-3 text-xs" data-testid={`ci-reserved-popover-${row.entry_id}`} onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between font-medium text-gray-700 mb-2">
          <span>{t("ci.ci_qty")}: <span className="font-mono">{ci}</span></span>
          <span>{t("ci.available")}: <span className="font-mono">{available}</span></span>
        </div>
        {other > 0 && mine > 0 && <p className="text-[10px] text-gray-500 mb-1">{t("ci.mine_note", { mine })}</p>}
        <div className="divide-y divide-gray-100 max-h-56 overflow-auto">
          {!detail && <p className="text-gray-400 py-2">…</p>}
          {detail?.items?.map((r) => (
            <div key={r.id} className="py-1.5 flex items-start justify-between gap-2" data-testid={`ci-reservation-row-${r.id}`}>
              <div className="min-w-0">
                <p className="truncate text-gray-800">{r.cart_name}</p>
                <p className="text-[10px] text-gray-500 truncate">{r.created_by_name} · {new Date(r.created_at).toLocaleDateString()}{r.note ? ` · ${r.note}` : ""}{r.expires_at ? ` · ${t("ci.expires")} ${r.expires_at.slice(0, 10)}` : ""}</p>
              </div>
              <span className="font-mono font-semibold text-gray-800 flex-shrink-0">{r.qty}</span>
              {canClear && <button type="button" onClick={() => clear(r)} data-testid={`ci-clear-${r.id}`} className="flex-shrink-0 text-[10px] px-1.5 py-0.5 rounded border border-red-200 text-red-600 hover:bg-red-50">{t("ci.clear")}</button>}
            </div>
          ))}
          {detail && detail.items.length === 0 && <p className="text-gray-400 py-2">{t("ci.none")}</p>}
        </div>
      </PopoverContent>
    </Popover>
  );
}
